import React from "react";
import {Grid} from "@mui/material";
import {Neighbourhood} from "../../api/Model";
import {SetState} from "../../util/FormHandlers";
import {
    calculateStatRanges,
    calculateVisitWithAssignedDesksStatistics,
    emptyStatRanges,
    StatRanges,
    VisitWithAssignedDesksStats
} from "../../components/visit-stat-block/OfficeVisitStats";
import {filterSerializer} from "../../util/FilterSerializer";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {StringEnumFilter} from "../../components/filter-fields/StringEnumFilter";
import {AssignedDeskCriteria, assignedDeskCriteria} from "../../components/visit-stat-block/AssignedDeskCriteria";
import {AssignedDesks} from "../../api/AssignedDeskStore";
import {numberCmp, stringCmp} from "../../util/SortUtil";
import {EmployeeVisitSheet} from "../../api/EmployeeVisitStore";
import {applyFilter, filterText} from "../../util/FilterUtil";
import {DateRangeFilter, DateRangeOption, DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";

export interface NeighbourhoodListFilterForm {
    search: string;
    dateRange: DateRangeOption;
    sortedBy: keyof typeof neighbourhoodSortOptions;
    assignedDeskCriteria: keyof typeof assignedDeskCriteria;
}

export const neighbourhoodSortOptions = {
    overflowDays: {name: 'Overflow Days'},
    maxDays: {name: 'Max Visits'},
    percentile90: {name: '90th Percentile'},
    medianDays: {name: 'Median Visits'},
    averageDays: {name: 'Average Visits'},
    name: {name: 'Name'},
    recommendedAssignedDeskCount: {name: 'Assigned Desk Recommendation'},
};

const sortOptionArray = Object.entries(neighbourhoodSortOptions)
    .map(([sortBy, sortOption]) => ({
        value: sortBy,
        label: sortOption.name,
    }));

const assignedDeskCriteriaOptions = (Object.entries(assignedDeskCriteria) as [keyof typeof assignedDeskCriteria, AssignedDeskCriteria][])
    .sort(numberCmp(([, it]: [any, AssignedDeskCriteria]) => it.ordinal, true))
    .map(([value, criteria]) => ({
        value: value,
        label: criteria.label,
    }));

const neighbourhoodStatsComparators: Record<keyof typeof neighbourhoodSortOptions, (a: NeighbourhoodStats, b: NeighbourhoodStats) => number> = {
    overflowDays: numberCmp('overflowDays', true),
    maxDays: numberCmp(it => it.maxVisits.percent, true),
    percentile90: numberCmp(it => it.percentile90.percent, true),
    medianDays: numberCmp(it => it.median.percent, true),
    averageDays: numberCmp(it => it.avgVisits.percent, true),
    name: stringCmp(it => it.neighbourhood.name),
    recommendedAssignedDeskCount: numberCmp(it => it.employeeStats.assignedDeskRecommendationEmployees.size, true),
};

export const neighbourhoodListFilterSerializer = filterSerializer<NeighbourhoodListFilterForm>(
    {
        search: '',
        dateRange: DateRangeOptions.lastThreeMonths,
        sortedBy: 'overflowDays',
        assignedDeskCriteria: 'envoy',
    },
    b => {
        b.str('search');
        b.dateRange('dateRange');
        b.str('sortedBy');
        b.str('assignedDeskCriteria');
    },
);

export interface NeighbourhoodStats extends VisitWithAssignedDesksStats {
    neighbourhood: Neighbourhood
}

export function filterNeighbourhoodVisits(
    employeeVisits: EmployeeVisitSheet,
    assignedDesks: AssignedDesks,
    rows: Neighbourhood[],
    filter: NeighbourhoodListFilterForm
): { filteredRows: NeighbourhoodStats[], statRanges: StatRanges } {
    if (rows.length === 0) {
        return {filteredRows: [], statRanges: emptyStatRanges}
    }

    const rowsWithStats = rows
        .map(neighbourhood => ({
            neighbourhood,
            ...calculateVisitWithAssignedDesksStatistics(
                employeeVisits,
                neighbourhood.employees,
                assignedDesks,
                assignedDeskCriteria[filter.assignedDeskCriteria],
                filter.dateRange,
                neighbourhood.desks.length,
            ),
        }));

    const filtered = applyFilter<NeighbourhoodStats>(
        rowsWithStats,
        filterText(filter.search, ({neighbourhood}) => [
            neighbourhood.name,
            neighbourhood.notes,
            neighbourhood.displayColor,
            ...neighbourhood.employees.map(employee => employee.fullName),
            ...neighbourhood.desks.map(desk => desk.name),
        ]),
    );

    return {
        filteredRows: filtered.sort(neighbourhoodStatsComparators[filter.sortedBy]),
        statRanges: calculateStatRanges(rowsWithStats.filter(it => it.neighbourhood.employees.length > 0)),
    };
}

export function NeighbourhoodListFilter({filter, setFilter}: {
    filter: NeighbourhoodListFilterForm,
    setFilter: SetState<NeighbourhoodListFilterForm>,
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <StringEnumFilter label="Sort by" filter={filter} setFilter={setFilter} property="sortedBy"
                              options={sortOptionArray}/>
            <DateRangeFilter filter={filter} setFilter={setFilter} property="dateRange"/>
            <StringEnumFilter label="Assign Desk From" filter={filter} setFilter={setFilter}
                              property="assignedDeskCriteria"
                              headerOption="Visit percent to recommend an assigned desk"
                              options={assignedDeskCriteriaOptions}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={neighbourhoodListFilterSerializer.emptyFilter} setFilter={setFilter}/>
        </Grid>
    );
}
